import React, { ReactElement, useState } from 'react';
import {
  Pressable, StyleSheet, Text, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import EllipsisIcon from '../../../components/EllipsisIcon';
import AlertModal from '../../../components/AlertModal';
import AddTemplateGroupModal from '../../../components/AssetManagement/AddTemplateGroupModal';
import updateTemplateGroup from '../../../requests/Assets/UpdateTemplateGroup';
import { logEvent } from '../../../components/LogEvent';
import Style from '../../../constants/Style';
import {
  DELETE_TEMPLATE_GROUP_SUCCESS, TEMPLATE_GROUP_NAME_DUPLICATE, TEMPLATE_GROUP_NAME_EXCESS,
  TEMPLATE_GROUP_NAME_INVALID, templateGroupNameMaxLength,
} from '../../../constants/AlertMessages';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import { UserPermission, TemplateGroup } from '../../../constants/Types';
import isMobile from '../../../components/IsMobile';
import SelectionModal from '../../../components/SelectionModal';
import CustomTextField from '../../../components/CustomTextField';
import nameRegex from '../../../components/Regex';
import SuccessMessage from '../../../components/SuccessMessage';
import Button from '../../../components/Button';
import AlertIconMobile from '../../../components/16pxIcons/AlertIconMobile';

interface TemplateGroupMenuItemProps {
  group: TemplateGroup;
  selectedGroupId: string;
  onPress: () => void;
  contextMenuOpen: () => void;
  contextMenuClose: () => void;
  currentContextMenu: string;
  templateGroups: TemplateGroup[];
  update;
  showScreenAlert;
}

export default function TemplateGroupRow({
  group, selectedGroupId, onPress, contextMenuOpen, contextMenuClose, currentContextMenu, update,
  templateGroups, showScreenAlert,
}: TemplateGroupMenuItemProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [editGroupModalVisible, setEditGroupModalVisible] = useState<boolean>(false);
  const [deleteConfirmationShown, setDeleteConfirmationShown] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [groupName, setGroupName] = useState<string | undefined>(group.Name);
  const [inputError, setInputError] = useState<string>('');
  const [updateError, setUpdateError] = useState<string>('');
  const [updateComplete, setUpdateComplete] = useState<boolean>(false);
  const groupId: string = group.ButtonPrototypeGroupId;
  const selected: boolean = selectedGroupId === groupId;

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (errorText: string) => showAlert(
    'Error',
    errorText ?? 'Could not delete template group. Please try again later.',
  );

  const editTemplateGroup: () => void = () => {
    logEvent('TemplateGroupRowWeb', 'Edit_Group');
    setEditGroupModalVisible(!editGroupModalVisible);
    if (!isMobile) contextMenuClose();
  };

  const promptDeletion: () => void = () => {
    logEvent('TemplateGroupRowWeb', 'Delete_Group');
    if (isMobile) {
      setDeleteConfirmationShown(true);
      return;
    }
    showAlert(
      'Delete template group',
      <>
        {'Are you sure you want to delete the template group '}
        <Text style={Style.alertBoldText}>{group.Name}</Text>
      </>,
      true,
      deleteTemplateGroup,
      'Delete',
    );
  };

  const deleteTemplateGroup: () => void = () => {
    contextMenuClose();
    setLoading(true);
    updateTemplateGroup(null, groupId, true).then((res) => {
      if (res && !res.isError) {
        showScreenAlert(
          DELETE_TEMPLATE_GROUP_SUCCESS,
          <>
            <Text style={Style.alertBoldText}>{group.Name}</Text>
            {' was successfully deleted'}
          </>,
          false,
        );
        update(null, groupId, false, true);
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const validateInput = (text: string) => {
    setUpdateError('');
    if (!(text.trim().length > 0)) setInputError('Can\'t be empty');
    else if (text.trim().length > templateGroupNameMaxLength) {
      setInputError(TEMPLATE_GROUP_NAME_EXCESS);
    } else if (!(nameRegex.exp.test(text.trim()))) {
      setInputError(TEMPLATE_GROUP_NAME_INVALID);
    } else if (templateGroups?.filter((templateGroup: TemplateGroup) => (groupId
      !== templateGroup.ButtonPrototypeGroupId)
      && templateGroup.Name?.trim().toLocaleLowerCase() === text.trim().toLocaleLowerCase())
      .length > 0) {
      setInputError(TEMPLATE_GROUP_NAME_DUPLICATE);
    } else setInputError('');
  };

  const restoreDefaults: () => void = () => {
    setEditGroupModalVisible(false);
    setGroupName(group.Name);
    setInputError('');
    setUpdateError('');
    setUpdateComplete(false);
  };

  const updateGroup: () => void = () => {
    setLoading(true);
    updateTemplateGroup(groupName.trim(), groupId).then((response) => {
      let isValid: boolean = response && !response.isError;
      let newID: string = groupId;
      if (!newID) {
        newID = response?.msg?.ButtonPrototypeGroupId;
        isValid = newID?.length > 0;
      }
      if (isValid) {
        update(groupName.trim(), groupId ?? newID, !groupId);
        setUpdateComplete(true);
      } else setUpdateError(response?.errorText ?? 'Unable to save changes');
    }).catch((error) => setUpdateError(error?.message ?? 'Unable to save changes'))
      .finally(() => setLoading(false));
  };

  const Menu = () => <View style={styles.menu}>
    {canEditTemplate && <Pressable
      style={({ hovered, pressed }: PressableState) => [
        styles.contextItem,
        hovered && Style.listItemHovered, pressed && Style.listItemPressed,
      ]}
      onPress={editTemplateGroup}
      testID='tmplt-group-menu-edit-btn'
    >
      <Text style={styles.contextItemText}>{'Edit'}</Text>
    </Pressable>}
    {canDeleteTemplate && <Pressable
      style={({ hovered, pressed }: PressableState) => [
        styles.contextItem,
        hovered && Style.listItemHovered, pressed && Style.listItemPressed,
      ]}
      onPress={promptDeletion}
      testID='tmplt-group-menu-delete-btn'
    >
      <Text style={styles.contextItemText}>{'Delete'}</Text>
    </Pressable>}
  </View>;

  return <>
    <Pressable
      onPress={onPress}
      testID='template-group-row'
      style={({ hovered, pressed }: PressableState) => [
        styles.container, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
        selected && styles.containerSelected,
      ]}
    >
      <Text
        testID='template-group-row-name'
        style={[styles.sectionTitle, selected && styles.sectionTitleSelected]}
      >
        {templateGroups?.filter((g: TemplateGroup) => g.ButtonPrototypeGroupId
          === group?.ButtonPrototypeGroupId)[0]?.Name}
      </Text>
      {(canEditTemplate || canDeleteTemplate) && <>
        <PressableStyled
          hitSlop={{ left: 40 }}
          style={styles.ellipsis}
          onPress={() => {
            logEvent('TemplateGroupRowWeb', 'Ellipsis');
            if (currentContextMenu === group.ButtonPrototypeGroupId) {
              contextMenuClose();
            } else contextMenuOpen();
          }}
          testID='am-template-group-menu-btn'
        >
          <EllipsisIcon
            fill={selected ? Colors.light.hereworksDarkBlue100 : Colors.light.hereworksDarkBlue400}
            style={{ marginRight: 18 }}
          />
        </PressableStyled>
        {!isMobile && currentContextMenu === group.ButtonPrototypeGroupId && <Menu />}
      </>}
    </Pressable>
    {isMobile ? <SelectionModal
      isVisible={currentContextMenu === group.ButtonPrototypeGroupId}
      hide={contextMenuClose}
      title={group.Name}
      listContent={(editGroupModalVisible || deleteConfirmationShown)
        ? <View style={styles.content}>
          {updateComplete && <SuccessMessage
            title={`The template group${groupId ? '\'s name' : ''} has been ${groupId ? 'updated' : 'added'}`}
            testID='successfully-created-templategroup-modal'
          />}
          {!updateComplete && editGroupModalVisible && <CustomTextField
            title='Name'
            value={groupName}
            maxLength={templateGroupNameMaxLength}
            updateValue={(text: string) => {
              setGroupName(text);
              validateInput(text);
            }}
            placeholder='Enter name'
            invalid={!!inputError}
            errorText={updateError || inputError}
          />}
          {deleteConfirmationShown && <View style={styles.deleteConfirmation}>
            <View style={styles.deleteIconContainer}>
              <AlertIconMobile />
            </View>
            <Text
              style={[styles.deleteConfirmationTitle, { color: styles.deleteButton.borderColor }]}
            >
              {'Delete template group?'}
            </Text>
            <Text style={styles.deleteConfirmationSubtitle}>
              {`Are you sure you want to delete the template group ${group.Name}? This action cannot be undone.`}
            </Text>
            <Button
              title='Delete'
              onPress={deleteTemplateGroup}
              style={{ ...styles.button, ...styles.deleteButton }}
              textStyle={{ color: styles.deleteButton.borderColor }}
            />
            <Button
              title='Cancel'
              onPress={contextMenuClose}
              style={styles.button}
            />
          </View>}
        </View> : <Menu />}
      buttonTitle={editGroupModalVisible ? 'Save' : undefined}
      apply={editGroupModalVisible ? updateGroup : undefined}
      onModalHide={restoreDefaults}
      onModalWillShow={restoreDefaults}
      {...{ loading }}
      style={{ maxHeight: undefined }}
    /> : <AddTemplateGroupModal
      isVisible={editGroupModalVisible}
      hide={() => setEditGroupModalVisible(false)}
      id={groupId}
      currentName={group.Name}
      {...{ update, templateGroups }}
    />}
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      {...{ loading }}
      setModalVisible={setAlertVisible}
    />
  </>;
}

const styles = StyleSheet.create({
  container: isMobile ? {
    ...Style.row,
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    marginTop: 8,
    marginHorizontal: 24,
  } : {
    ...Style.row,
    zIndex: -1,
    borderRadius: 8,
    marginHorizontal: 12,
    height: 46,
    padding: 12,
    paddingEnd: 18,
  },
  containerSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  button: {
    alignSelf: 'stretch',
  },
  deleteButton: {
    marginBottom: 12,
    borderColor: Colors.light.hereworksRed500,
  },
  deleteConfirmation: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteConfirmationTitle: {
    fontSize: 22,
    letterSpacing: 0,
    fontFamily: 'Poppins_600SemiBold',
    marginTop: 24,
    marginBottom: 12,
  },
  deleteConfirmationSubtitle: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 80,
    letterSpacing: 0,
  },
  deleteIconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    marginTop: 56,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksRed500,
  },
  sectionTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack500,
  },
  sectionTitleSelected: {
    color: '#FFF',
  },
  contextItem: {
    paddingVertical: 9,
    backgroundColor: '#fff',
    borderRadius: isMobile ? 8 : undefined,
  },
  contextItemText: {
    marginLeft: 12,
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
  },
  ellipsis: {
    position: 'absolute',
    right: 0,
    justifyContent: 'center',
    height: '100%',
  },
  content: {
    marginBottom: 16,
  },
  menu: isMobile ? {} : {
    zIndex: 5,
    position: 'absolute',
    top: 0,
    right: 50,
    width: 87,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
});
